import axios from 'axios';
import { ProjectMode, SyncEnvironmentResponse, ProjectVisibility } from '@optidev/shared-types';

import { apiClient, createAuthHeaders } from '../api-client';
import { firebaseAuth } from '../firebase';
import { markStepIfNeeded } from './onboarding';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export interface Project {
  id: string;
  workspaceId: string;
  createdBy: string;
  name: string;
  urlSlug: string;
  description?: string;
  templateId?: string;
  currentVersionId?: string;
  mode: ProjectMode;
  devContainerId?: string;
  containerPath?: string;
  previewPort?: string;
  connectionIds: string[];
  metadata: Record<string, any>;
  visibility?: ProjectVisibility;
  createdAt: Date;
  updatedAt: Date;
  // GitHub Integration
  githubConnectionId?: string;
  githubRepoUrl?: string;
  githubRepoOwner?: string;
  githubRepoName?: string;
  githubDefaultBranch?: string;
  githubSyncEnabled?: boolean;
  // GitLab Integration
  gitlabConnectionId?: string;
  gitlabRepoUrl?: string;
  gitlabRepoOwner?: string;
  gitlabRepoName?: string;
  gitlabDefaultBranch?: string;
  gitlabSyncEnabled?: boolean;
  gitlabProjectId?: number;
  // Preview Build
  latestPreviewBuildId?: string;
  latestPreviewBuildNumber?: number;
  latestPreviewUrl?: string;
  // Publish
  latestPublishUrl?: string;
  // Cross-Project Referencing
  allowCrossReference?: boolean;
}

export interface CreateProjectPayload {
  name?: string;
  urlSlug?: string;
  description?: string;
  mode?: ProjectMode;
  templateId?: string;
  connectionIds?: string[];
  metadata?: Record<string, any>;
}

export const projectsApi = {
  /**
   * Create a new project
   */
  async create(token: string, payload: CreateProjectPayload): Promise<Project> {
    const response = await apiClient.post('/api/editor/projects', payload, {
      headers: createAuthHeaders(token),
    });
    // Fire-and-forget onboarding step 1 mark
    const uid = firebaseAuth.currentUser?.uid;
    if (uid) markStepIfNeeded('step1', token, uid);
    return response.data;
  },

  /**
   * Get all projects for the authenticated user's company
   */
  async getAll(token: string): Promise<Project[]> {
    const response = await apiClient.get('/api/editor/projects', {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Get a single project by ID
   */
  async getOne(token: string, id: string): Promise<Project> {
    const response = await apiClient.get(`/api/editor/projects/${id}`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Sync project environment variables (connections + secrets) to container workspace
   */
  async syncEnvironment(token: string, projectId: string): Promise<SyncEnvironmentResponse> {
    const response = await apiClient.post(
      `/api/editor/projects/${projectId}/sync-environment`,
      {},
      {
        headers: createAuthHeaders(token),
      }
    );
    return response.data;
  },

  /**
   * Check if a URL slug is available
   */
  async checkSlugAvailability(token: string, slug: string): Promise<{ available: boolean }> {
    const response = await apiClient.get(
      `/api/editor/projects/check-slug/${encodeURIComponent(slug)}`,
      {
        headers: createAuthHeaders(token),
      }
    );
    return response.data;
  },

  /**
   * Update project URL slug
   */
  async updateUrlSlug(token: string, projectId: string, urlSlug: string): Promise<Project> {
    const response = await apiClient.put(
      `/api/editor/projects/${projectId}/url-slug`,
      { urlSlug },
      {
        headers: createAuthHeaders(token),
      }
    );
    return response.data;
  },

  /**
   * Get active Claude session ID (multi-device sync)
   * Replaces localStorage: claude-session-{devContainerId}-{projectId}
   */
  async getActiveSession(
    token: string,
    projectId: string
  ): Promise<{ activeClaudeSessionId: string | null; projectId: string }> {
    const response = await apiClient.get(
      `/api/editor/projects/${projectId}/active-session`,
      {
        headers: createAuthHeaders(token),
      }
    );
    return response.data;
  },

  /**
   * Update active Claude session ID
   * Replaces localStorage: claude-session-{devContainerId}-{projectId}
   */
  async updateActiveSession(token: string, projectId: string, sessionId: string | null): Promise<{ activeClaudeSessionId: string | null; projectId: string }> {
    const response = await apiClient.patch(
      `/api/editor/projects/${projectId}/active-session`,
      { sessionId },
      {
        headers: createAuthHeaders(token),
      }
    );
    return response.data;
  },

  /**
   * Get builder data (initial prompt, refined prompt, etc.)
   * Replaces localStorage: builder-data-{projectId}
   */
  async getBuilderData(token: string, projectId: string): Promise<{ builderData: Record<string, any>; builderDataProcessed: boolean; projectId: string }> {
    const response = await apiClient.get(
      `/api/editor/projects/${projectId}/builder-data`,
      {
        headers: createAuthHeaders(token),
      }
    );
    return response.data;
  },

  /**
   * Update builder data
   * Replaces localStorage: builder-data-{projectId}
   */
  async updateBuilderData(token: string, projectId: string, builderData: Record<string, any>): Promise<{ builderData: Record<string, any>; projectId: string }> {
    const response = await apiClient.patch(
      `/api/editor/projects/${projectId}/builder-data`,
      builderData,
      {
        headers: createAuthHeaders(token),
      }
    );
    return response.data;
  },

  /**
   * Mark builder data as processed to prevent re-execution on project reopen
   */
  async markBuilderDataProcessed(token: string, projectId: string): Promise<{ builderDataProcessed: boolean; projectId: string }> {
    const response = await apiClient.patch(
      `/api/editor/projects/${projectId}/builder-data-processed`,
      {},
      {
        headers: createAuthHeaders(token),
      }
    );
    return response.data;
  },

  /**
   * Remove a connection from a project's connectionIds
   */
  async removeConnection(token: string, projectId: string, connectionId: string): Promise<void> {
    await apiClient.delete(
      `/api/editor/projects/${projectId}/connections/${connectionId}`,
      {
        headers: createAuthHeaders(token),
      }
    );
  },

  /**
   * Rename project - updates both display name and URL slug
   * For unpublished projects: instant rename
   * For published projects: auto-migrates R2 files to new slug (no republish needed)
   */
  async rename(
    token: string,
    projectId: string,
    name: string
  ): Promise<{
    project: Project;
    requiresRepublish: boolean;
    oldUrl?: string;
    newUrl: string;
    message: string;
    warning?: string;
  }> {
    const response = await apiClient.patch(
      `/api/editor/projects/${projectId}/rename`,
      { name },
      {
        headers: createAuthHeaders(token),
      }
    );
    return response.data;
  },
};
